import { inject, Injectable } from '@angular/core';
import { Routes } from '@angular/router';
import { ICbdItem, ILink, ISubcategory } from '../interfaces/ICbdItem';
import GENERAL_ROUTES from 'projects/general/src/app/general.routes';
import CONTROLLING_ROUTES from 'projects/controlling/src/app/controlling.routes';
import PLANNING_ROUTES from 'projects/planning/src/app/planning.routes';
import WOOD_SUPPLY_ROUTES from 'projects/woodsupply/src/app/wood-supply.routes';
import WOOD_SECURITY_ROUTES from 'projects/woodsecurity/src/app/wood-security.routes';
import { SystemVersion } from 'projects/shared/src/lib/zn-version/services/system-version';

interface RouteWithMenu {
  route: any;
  modulePath: string;
}

@Injectable({ providedIn: 'root' })
export class MenuGeneratorService {
  private readonly moduleRoutes: { path: string; routes: Routes }[] = [
    { path: 'general', routes: GENERAL_ROUTES },
    { path: 'controlling', routes: CONTROLLING_ROUTES },
    { path: 'planning', routes: PLANNING_ROUTES },
    { path: 'wood-supply', routes: WOOD_SUPPLY_ROUTES },
    { path: 'wood-security', routes: WOOD_SECURITY_ROUTES },
  ];
  private readonly versionService = inject(SystemVersion);

  generateMenu(): ICbdItem[] {
    const routesWithMenu = this.collectRoutesWithMenu();
    const grouped = this.groupByCategory(routesWithMenu);
    const result = this.buildMenuStructure(grouped);
    this.versionService.getAppVersion();

    return result;
  }

  private collectRoutesWithMenu(): RouteWithMenu[] {
    const results: RouteWithMenu[] = [];

    for (const module of this.moduleRoutes) {
      for (const route of module.routes) {
        if (route.data?.showInMenu === true) {
          results.push({
            route,
            modulePath: module.path,
          });
        }
      }
    }

    return results;
  }

  private groupByCategory(
    routes: RouteWithMenu[]
  ): Map<string, Map<string, RouteWithMenu[]>> {
    const categoryMap = new Map<string, Map<string, RouteWithMenu[]>>();

    for (const item of routes) {
      const category = item.route.data.menuCategory || 'Other';
      const subcategory = item.route.data.menuSubcategory || '';

      if (!categoryMap.has(category)) {
        categoryMap.set(category, new Map());
      }

      const subcategoryMap = categoryMap.get(category)!;
      if (!subcategoryMap.has(subcategory)) {
        subcategoryMap.set(subcategory, []);
      }

      subcategoryMap.get(subcategory)!.push(item);
    }

    return categoryMap;
  }

  private buildMenuStructure(
    categoryMap: Map<string, Map<string, RouteWithMenu[]>>
  ): ICbdItem[] {
    const menuItems: ICbdItem[] = [];

    for (const [category, subcategoryMap] of categoryMap.entries()) {
      const subcategories: ISubcategory[] = [];

      for (const [subcategoryKey, routes] of subcategoryMap.entries()) {
        const links: ILink[] = routes.map(item => ({
          title: item.route.data.menuTitle,
          url: `/${item.modulePath}/${item.route.path}`,
          requiredPolicy: item.route.data.requiredPolicy,
        }));

        if (subcategoryKey) {
          subcategories.push({
            title: subcategoryKey,
            links,
          });
        } else {
          links.forEach(link => {
            subcategories.push({
              title: undefined,
              links: [link],
            });
          });
        }
      }

      menuItems.push({
        title: category,
        subcategories,
      });
    }

    return menuItems;
  }
}
